import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexStroke,
  ApexTitleSubtitle,
  ApexXAxis,
} from 'ng-apexcharts';
import ApexCharts from 'apexcharts';
import { ToastrService } from 'ngx-toastr';
import { AppComponent } from 'src/app/app.component';
import { AlertDTO } from 'src/app/model/alerts/alert-dto';
import { AlertService } from 'src/app/services/alert.service';
import { LoginService } from 'src/app/services/login.service';
import cloneDeep from 'lodash.clonedeep';
import { NodeService } from 'src/app/services/node.service';
import { BackupService } from 'src/app/services/backup.service';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  stroke: ApexStroke;
  title: ApexTitleSubtitle;
  annotations: ApexAnnotations | undefined;
};

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  public unmanagedAlerts: AlertDTO[] | undefined;
  public alertDetectionsChart: ApexCharts | undefined;
  public months: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  public currentYear: number = new Date().getFullYear();
  public lastYear: number = new Date().getFullYear() - 1;
  public workingNodes: number | undefined;
  public validatedNodes: number | undefined;
  public totalNodes: number | undefined;
  public runningBackupSchedules: number | undefined;
  title = 'Dashboard';

  constructor(
    public loginService: LoginService,
    public alertService: AlertService,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    private readonly nodeService: NodeService,
    private readonly backupService: BackupService,
  ) {
    AppComponent.updateAppHeader('dashboard');
  }

  ngOnInit() {
    if (!this.loginService.isAuthenticated()) {
      this.loginService.goToLogin();
    } else {
      this.getAlertInfo();
      this.getAlertDetectionsCurrentYear();
      this.getAlertDetectionsLastYear();
      this.loadNodeInfo();
      this.loadBackupsInfo();
    }
  }

  private loadBackupsInfo(): void {
    this.backupService
      .getBackupSchedules()
      .then((response) => {
        this.runningBackupSchedules = response.items.filter(
          (x) => x.enabled,
        ).length;
      })
      .catch(() => {
        this.toastr.error('Error loading backup schedules');
      });
  }

  private loadNodeInfo(): void {
    this.nodeService
      .getNodes(1, 99999, 'Id', true, true)
      .then((response) => {
        this.workingNodes = response.items.length;
      })
      .catch(() => {
        this.toastr.error('Error loading nodes');
      });

    this.nodeService
      .getNodes(1, 99999, 'Id')
      .then((response) => {
        this.totalNodes = response.items.length;
        this.validatedNodes = response.items.filter(
          (x) => x.validated === true,
        ).length;
      })
      .catch(() => {
        this.toastr.error('Error loading nodes');
      });
  }

  private getAlertInfo(): void {
    this.alertService
      .getAlerts(
        1,
        9999,
        undefined,
        new Date(1999, 0, 1).toDateString(),
        new Date(2099, 11, 31).toDateString()
      )
      .then((response) => {
        this.unmanagedAlerts = response.items;
      })
      .catch(() => {
        this.toastr.error('Error loading alerts');
      });
  }

  private getAlertDetectionsCurrentYear(): void {
    let fromDate = new Date();
    fromDate.setDate(1);
    fromDate.setMonth(0);
    let toDate = new Date();
    toDate.setDate(0);
    toDate.setMonth(0);
    toDate.setFullYear(toDate.getFullYear() + 1);

    this.alertService
      .getAlerts(
        1,
        9999,
        undefined,
        fromDate.toDateString(),
        toDate.toDateString(),
        true,
      )
      .then((response) => {
        let alertDetectionsThisYear: any = [];

        this.months.forEach((month, index) => {
          alertDetectionsThisYear[index] = { x: month, y: 0 };
        });

        response.items.forEach((alert) => {
          const mes = new Date(alert.date).getMonth();
          alertDetectionsThisYear[mes] ??= { x: mes, y: 0 };

          alertDetectionsThisYear[mes].y++;
        });

        let chartOptions = cloneDeep(this.alertDetectionsChartOptions);
        chartOptions.series[0].data = alertDetectionsThisYear;
        this.alertDetectionsChart = new ApexCharts(
          document.querySelector('#alertDetectionsChart'),
          chartOptions,
        );
        this.alertDetectionsChart.render();
      })
      .catch(() => {
        this.toastr.error('Error loading alerts');
      });
  }

  private getAlertDetectionsLastYear(): void {
    let fromDate = new Date();
    fromDate.setDate(1);
    fromDate.setMonth(0);
    fromDate.setFullYear(fromDate.getFullYear() - 1);
    let toDate = new Date();
    toDate.setMonth(0);
    toDate.setFullYear(toDate.getFullYear());
    toDate.setDate(0);

    this.alertService
      .getAlerts(
        1,
        9999,
        undefined,
        fromDate.toDateString(),
        toDate.toDateString(),
        true,
      )
      .then((response) => {
        let alertDetectionsLastYear: any = [];

        this.months.forEach((month, index) => {
          alertDetectionsLastYear[index] = { x: month, y: 0 };
        });

        response.items.forEach((alert) => {
          const mes = new Date(alert.date).getMonth();
          alertDetectionsLastYear[mes] ??= { x: mes, y: 0 };

          alertDetectionsLastYear[mes].y++;
        });

        let chartOptions = cloneDeep(this.alertDetectionsChartOptions);
        chartOptions.series[0].data = alertDetectionsLastYear;
        this.alertDetectionsChart = new ApexCharts(
          document.querySelector('#alertDetectionsLastChart'),
          chartOptions,
        );
        this.alertDetectionsChart.render();
      })
      .catch(() => {
        this.toastr.error('Error loading alerts');
      });
  }

  public navigateToAlerts(): void {
    this.router.navigate(['alerts']);
  }

  public navigateToNodeManager(): void {
    this.router.navigate(['node-manager']);
  }

  public alertDetectionsChartOptions = {
    series: [
      {
        name: `Alert detections this year`,
        data: undefined,
      },
    ],
    chart: {
      height: 150,
      width: 370,
      type: 'line',
      zoom: {
        enabled: false,
      },
      fontFamily: 'monospace',
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'straight',
    },
    title: {
      text: '',
      align: 'left',
    },
    grid: {
      row: {
        colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
        opacity: 0.5,
      },
    },
    xaxis: {
      type: 'category',
      labels: {
        show: true,
      },
    },
  };
}
